import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';

import { BOOKMARKED_QUESTIONS } from 'legacy/src/constants/common';
import OverviewIcon from '../OverviewIcon';
import { Storage } from '../../utility/Storage';
import './styles.scss';

/**
 * Renders the top questions overview
 * */
class QuestionsOverview extends PureComponent {
  static displayName = 'QuestionsOverview';

  responses = {};

  static propTypes = {
    /**
     * Flag for displaying desktop view or mobile view icon.
     * The submit button icon is different on desktop vs mobile.
     * */
    isMobile: PropTypes.bool,
    /**
     * Handler for closing the questions menu and the main menu.
     * */
    closeMenuContent: PropTypes.func,
    /**
     * Flag for the player being in the preview mode or nor.
     * */
    preview: PropTypes.bool,
    isImpersonating: PropTypes.bool,
  };

  static defaultProps = {
    isMobile: false,
    closeMenuContent: null,
    preview: false,
    isImpersonating: false,
  };

  constructor(props) {
    super(props);

    const {
      learnosityData: { app, items: propItems },
      isMobile,
      preview,
    } = props;
    const items = isMobile ? app.getItems() : propItems;
    const itemKeys = Object.keys(items);
    let answeredQuestions = [];

    if (!preview) {
      answeredQuestions = itemKeys.filter(
        (itemKey) => items[itemKey].attempt_status !== 'not_attempted',
      );
    }

    this.state = {
      refresh: false,
      loading: true,
      answeredQuestions,
    };

    /**
     * Current item index used for first time
     * */
    this.currentItemIndex = 1;
    /**
     * Icons width - must include the connection line
     * Used to calculate max nr of icons allowed - based on container width
     * */
    this.iconWidth = 46;

    this.resizeHandler = this.resizeHandler.bind(this);
    this.getIconsPerPage = this.getIconsPerPage.bind(this);
    this.getIndexByReference = this.getIndexByReference.bind(this);
    this.getMaxIndex = this.getMaxIndex.bind(this);
    this.getFromIndex = this.getFromIndex.bind(this);
    this.getToIndex = this.getToIndex.bind(this);
    this.onIconClick = this.onIconClick.bind(this);
    this.itemChanging = this.itemChanging.bind(this);
    this.itemAttempted = this.itemAttempted.bind(this);
  }

  componentDidMount() {
    /**
     * Hackish fix to support transition from mobile to desktop
     * */
    setTimeout(() => {
      this.readyListener();
    }, 0);
  }

  /**
   * Init method
   * */
  readyListener() {
    const {
      learnosityData: { items, activity, app },
      preview,
      isImpersonating,
    } = this.props;
    app.on('item:changing', this.itemChanging);

    if (!preview) {
      app.on('item:setAttemptedResponse', this.itemAttempted);
    }

    const containerElement = document.querySelector('.top-questions-overview');
    if (containerElement) {
      this.container = containerElement;
      this.iconsPerPage = this.getIconsPerPage();
    }

    /**
     * Define store for persisting user local settings
     * */
    const { session_id } = activity;
    this.bookmarkStore = new Storage(
      `${BOOKMARKED_QUESTIONS}-${session_id}${isImpersonating ? '-i' : ''}`,
    );

    this.itemReferences = Object.keys(items);

    this.itemsLength = this.itemReferences.length;

    /*
     * currentItem must be took with app.getCurrentItem() to support mobile component unmount
     * every time the menu is closed.
     * */
    this.currentItem = app.getCurrentItem();
    this.currentItemIndex = this.getIndexByReference(
      this.currentItem.reference,
    );

    window.addEventListener('resize', this.resizeHandler);

    this.setState({
      loading: false,
    });
  }

  componentWillUnmount() {
    const {
      learnosityData: { app },
      preview,
    } = this.props;

    app.off('item:changing', this.itemChanging);

    if (!preview) {
      app.off('item:setAttemptedResponse', this.itemAttempted);
    }

    window.removeEventListener('resize', this.resizeHandler);
  }

  /*
   * Calculate allowed icons per page based on the available space
   * */
  getIconsPerPage() {
    /**
     * Remove 20 pixels because the container gets smaller
     * after learnosity has done loading the navigation bar
     * */
    return Math.floor((this.container.offsetWidth - 20) / this.iconWidth);
  }

  /**
   * Called when learnosity is setting the current item
   * @param {number} itemIndex The item index
   * */
  itemChanging(itemIndex) {
    const {
      learnosityData: { app },
    } = this.props;
    this.currentItem = app.getCurrentItem();
    this.currentItemIndex = itemIndex + 1;

    this.refresh();
  }

  itemAttempted(reference) {
    const { answeredQuestions } = this.state;

    if (!answeredQuestions.includes(reference)) {
      this.setState((prevState) => ({
        answeredQuestions: [...prevState.answeredQuestions, reference],
      }));
    }
  }

  /**
   * Get index by reference
   * @param {string} reference The question reference
   * */
  getIndexByReference(reference) {
    const {
      learnosityData: { items },
    } = this.props;
    return Object.keys(items).indexOf(reference) + 1;
  }

  /*
   * Resize handler - resize components
   * */
  resizeHandler() {
    this.refresh();
  }

  /**
   * Refresh component
   * */
  refresh() {
    this.setState(
      (prevState) => ({
        refresh: !prevState.refresh,
      }),
      () => {
        if (this.container) {
          this.iconsPerPage = this.getIconsPerPage();
        }
      },
    );
  }

  /**
   * Get maximum index
   * */
  getMaxIndex() {
    return this.itemsLength > this.iconsPerPage
      ? this.iconsPerPage
      : this.itemsLength;
  }

  /**
   * Get from index
   * @param {number} maxIndex Maximum index
   * */
  getFromIndex(maxIndex) {
    if (this.currentItemIndex > maxIndex) {
      return this.currentItemIndex - this.iconsPerPage + 1;
    }
    return 1;
  }

  /**
   * Get to index
   * */
  getToIndex(maxIndex) {
    if (this.currentItemIndex > maxIndex) {
      return this.currentItemIndex;
    }
    return maxIndex;
  }

  /**
   * Click icon event handler - callback
   * @param {object} event The click event handler
   * @param {string} reference The clicked icon question reference
   * */
  onIconClick(event, reference) {
    const {
      learnosityData: { app },
    } = this.props;
    const itemsObj = app.items();
    itemsObj.goto(reference);
  }

  /**
   * Keydown handler to allow use of
   * - arrows to navigate left and right
   * - spacebar and return to select question
   */
  keyDownHandler = (event, reference) => {
    // only handle pressing of left and right arrow keys, spacebar and return
    if ([37, 39, 13, 32].includes(event.keyCode)) {
      // Make sure to stop event bubbling
      event.preventDefault();
      event.stopPropagation();

      const {
        learnosityData: { app },
      } = this.props;
      const references = Object.keys(app.getItems());
      if (
        (event.keyCode === 37 && references.indexOf(references) <= 0) ||
        (event.keyCode === 39 &&
          references.indexOf(references) >= references.length - 1)
      ) {
        return;
      }
      this.onIconClick(_, reference);
    }
  };

  render() {
    const { loading, answeredQuestions } = this.state;
    const { isMobile, closeMenuContent } = this.props;

    if (loading) {
      return null;
    }

    const maxIndex = this.getMaxIndex();
    const from = this.getFromIndex(maxIndex);
    const to = this.getToIndex(maxIndex);

    let icons = [];
    const bookmarkedQuestions = this.bookmarkStore.getQuestions();
    const { itemReferences } = this;

    for (let i = from; i <= to; i++) {
      const reference = itemReferences[i - 1];
      const isAnswered = answeredQuestions.includes(reference);
      const isBookmarked = bookmarkedQuestions.includes(reference);
      const isSelected = this.currentItemIndex === i;
      const isLast = this.itemsLength === i;

      icons.push(
        <OverviewIcon
          isMobile={isMobile}
          index={i}
          reference={reference}
          isAnswered={isAnswered}
          isSelected={isSelected}
          hasConnection={!isLast}
          isBookmarked={isBookmarked}
          onClick={this.onIconClick}
          keyDownHandler={this.keyDownHandler}
          key={`icon-${i}`}
          closeMenuContent={closeMenuContent}
        />,
      );
    }

    return (
      <div className="question-list" role="tablist">
        {icons}
      </div>
    );
  }
}

export default QuestionsOverview;
